// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { URL } from "../../service/Service";
import {
    BellIcon,
    CheckCircleIcon,
    PencilSquareIcon,
    UserPlusIcon,
    XCircleIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "../../i18n/LanguageContext";

const getNotificationId = (notification) => {
    return (
        notification?.notificationID ||
        notification?.notificationId ||
        notification?.id ||
        notification?.notification?.notificationID ||
        null
    );
};

const getStatusToFilter = (notification) => {
    if (notification?.metadata?.customerStatusUpdate) {
        return notification.metadata.customerStatusUpdate;
    }

    return notification?.metadata?.customerStatus;
};

const getCustomerName = (notification, fallback = "-") => {
    return notification?.metadata?.customerName || fallback;
};

const getNotificationContent = (notification, t) => {
    const status = getStatusToFilter(notification);
    const customerName = getCustomerName(notification);
    const isUpdate = status?.startsWith("UPDATE_");
    const approved = status === "APPROVED" || status === "UPDATE_APPROVED";

    return {
        approved,
        isUpdate,
        title: approved
            ? `${isUpdate ? t('notifications.update') : t('notifications.creation')} — ${t('notifications.approved')}`
            : `${isUpdate ? t('notifications.update') : t('notifications.creation')} — ${t('notifications.rejected')}`,
        description: `${isUpdate ? t('notifications.update') : t('notifications.creation')}: ${customerName} — ${approved ? t('notifications.approved') : t('notifications.rejected')} (Eupago).`,
    };
};

const formatDateTime = (value, language, unavailable) => {
    if (!value) return unavailable;
    const parsed = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat(language === 'pt' ? "pt-PT" : language, {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(parsed);
};

const NotificationsPartner = () => {
    const { t, language } = useTranslation();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [markingAsRead, setMarkingAsRead] = useState(false);
    const [error, setError] = useState("");
    const [currentTab, setCurrentTab] = useState("APPROVED");

    const jwtToken = sessionStorage.getItem("jwt");

    const statusGroups = useMemo(
        () => ({
            APPROVED: ["APPROVED", "UPDATE_APPROVED"],
            REJECTED: ["REJECTED", "UPDATE_REJECTED"],
        }),
        []
    );

    const markNotificationsAsRead = useCallback(async (items) => {
        if (!jwtToken) return;

        const notificationIds = items
            .map((notification) => getNotificationId(notification))
            .filter(Boolean);

        if (notificationIds.length === 0) {
            window.dispatchEvent(new Event("notifications:changed"));
            window.dispatchEvent(new Event("notifications:refresh"));
            return;
        }

        try {
            setMarkingAsRead(true);

            await Promise.all(
                notificationIds.map((id) =>
                    axios.put(
                        `${URL}/api/user/notifications/${id}/read`,
                        {},
                        {
                            headers: {
                                Authorization: `Bearer ${jwtToken}`,
                            },
                        }
                    )
                )
            );

            window.dispatchEvent(new Event("notifications:changed"));
            window.dispatchEvent(new Event("notifications:refresh"));
        } catch (error) {
            console.error("Erro ao marcar notificações como lidas:", error);
        } finally {
            setMarkingAsRead(false);
        }
    }, [jwtToken]);

    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const response = await axios.get(`${URL}/api/user/notifications`, {
                headers: {
                    Authorization: `Bearer ${jwtToken}`,
                },
            });

            const data = Array.isArray(response.data) ? response.data : [];

            setNotifications(data);

            await markNotificationsAsRead(data);
        } catch (error) {
            console.error("Erro ao carregar notificações:", error);
            setError(t('notifications.loadError'));
        } finally {
            setLoading(false);
        }
    }, [jwtToken, markNotificationsAsRead, t]);

    useEffect(() => {
        if (!jwtToken) {
            setError(t('auth.sessionExpired'));
            setLoading(false);
            return;
        }

        fetchNotifications();
    }, [jwtToken, fetchNotifications, t]);

    const filteredNotifications = useMemo(() => {
        return notifications.filter((notification) =>
            statusGroups[currentTab].includes(getStatusToFilter(notification))
        );
    }, [notifications, currentTab, statusGroups]);

    const totals = useMemo(() => ({
        APPROVED: notifications.filter((item) =>
            statusGroups.APPROVED.includes(getStatusToFilter(item))
        ).length,
        REJECTED: notifications.filter((item) =>
            statusGroups.REJECTED.includes(getStatusToFilter(item))
        ).length,
    }), [notifications, statusGroups]);

    if (loading) {
        return (
            <div className="flex min-h-[300px] items-center justify-center">
                <p className="text-gray-500">{t('notifications.loading')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-[300px] items-center justify-center">
                <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                    {error}
                </p>
            </div>
        );
    }

    return (
        <main className="mx-auto max-w-6xl px-5 pb-12 pt-10 sm:px-8 sm:pt-12">
            <header className="mb-7 flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-cl ring-1 ring-green-100">
                    <BellIcon className="h-6 w-6" />
                </div>
                <div>
                    <h1 className="text-2xl font-black tracking-tight text-gray-950">{t('notifications.title')}</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        {t('notifications.partnerDescription')}
                    </p>

                    {markingAsRead && (
                        <p className="mt-2 text-xs font-semibold text-green-cl">{t('notifications.syncing')}</p>
                    )}
                </div>
            </header>

            <div className="mb-6 border-b border-gray-200">
                <nav className="flex gap-2" aria-label="Estado das notificações">
                    <button
                        type="button"
                        className={`mb-[-1px] inline-flex items-center gap-2 rounded-t-xl border-b-2 px-4 py-3 text-sm font-bold transition ${
                            currentTab === "APPROVED"
                                ? "border-green-cl bg-green-50 text-green-800"
                                : "border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                        }`}
                        onClick={() => setCurrentTab("APPROVED")}
                    >
                        <CheckCircleIcon className="h-4 w-4" /> {t('notifications.approvedPlural')}
                        <span className="rounded-full bg-white px-2 py-0.5 text-xs ring-1 ring-gray-200">{totals.APPROVED}</span>
                    </button>

                    <button
                        type="button"
                        className={`mb-[-1px] inline-flex items-center gap-2 rounded-t-xl border-b-2 px-4 py-3 text-sm font-bold transition ${
                            currentTab === "REJECTED"
                                ? "border-red-500 bg-red-50 text-red-700"
                                : "border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                        }`}
                        onClick={() => setCurrentTab("REJECTED")}
                    >
                        <XCircleIcon className="h-4 w-4" /> {t('notifications.rejectedPlural')}
                        <span className="rounded-full bg-white px-2 py-0.5 text-xs ring-1 ring-gray-200">{totals.REJECTED}</span>
                    </button>
                </nav>
            </div>

            <section className="space-y-4">
                {filteredNotifications.length > 0 ? (
                    filteredNotifications.map((notification, index) => {
                        const id = getNotificationId(notification) || index;
                        const metadata = notification?.metadata || {};
                        const content = getNotificationContent(notification, t);
                        const TypeIcon = content.isUpdate ? PencilSquareIcon : UserPlusIcon;

                        return (
                            <div
                                key={id}
                                className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${content.approved ? "border-green-100" : "border-red-100"}`}
                            >
                                <div className={`h-1 w-full ${content.approved ? "bg-green-500" : "bg-red-500"}`} />
                                <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-start sm:p-6">
                                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${content.approved ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                                        <TypeIcon className="h-6 w-6" />
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h2 className="text-base font-bold text-gray-950">{content.title}</h2>
                                            <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${content.approved ? "bg-green-50 text-green-700 ring-1 ring-green-200" : "bg-red-50 text-red-700 ring-1 ring-red-200"}`}>
                                                {content.approved ? t('notifications.approved') : t('notifications.rejected')}
                                            </span>
                                        </div>

                                        <p className="mt-2 text-sm leading-6 text-gray-600">{content.description}</p>

                                        {!content.approved && metadata.rejectionReason && (
                                            <div className="mt-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-800">
                                                <strong>{t('notifications.reason')}:</strong> {metadata.rejectionReason}
                                            </div>
                                        )}

                                        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-500">
                                            <span><strong className="text-gray-700">{t('clients.client')}:</strong> {getCustomerName(notification)}</span>
                                            <span><strong className="text-gray-700">CID:</strong> {metadata.customerID || "-"}</span>
                                            <time>{formatDateTime(notification.createAt, language, t('notifications.noDate'))}</time>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="flex min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center">
                        <BellIcon className="h-9 w-9 text-gray-300" />
                        <p className="mt-3 font-bold text-gray-700">{t('notifications.empty')}</p>
                        <p className="mt-1 text-sm text-gray-500">{t('notifications.emptyDescription')}</p>
                    </div>
                )}
            </section>
        </main>
    );
};

export default NotificationsPartner;
